import AuthSvcMod from '@Services/Auth.svc.js';
import DOMHelpersSvcMod from '@Services/DOMHelpers.svc.js';
import ColorSolver from './ColorSolver.svc.js';
import Color from './Colors.svc.js';
/** @typedef {import('./ColorTheming.svc')} Types */
//
export class Theming {
	constructor(DOMHelpersSvc, ColorSvc, ColorSolverSvc, AuthSvc, colorVars) {
		'ngInject';
		this.colorService = ColorSvc;
		this.colorSolverService = ColorSolverSvc;
		this.domHelpersSvc = DOMHelpersSvc;
		this.AuthSvc = AuthSvc;
		this.colorVars = colorVars;
		// Meta theme-color value for dark mode (our choice)
		this.META_THEME_DARK = '#212529';
	}

	/**
	 * @param {string} color hex colour
	 * @param {number} percent positive to lighten, negative to darken
	 * @returns {string} adjusted hex colour
	 */
	shadeColor(color, percent) {
		const f = parseInt(color.slice(1), 16);
		const t = percent < 0 ? 0 : 255;
		const p = percent < 0 ? percent * -1 : percent;
		const R = f >> 16;
		const G = (f >> 8) & 0x00ff;
		const B = f & 0x0000ff;
		return `#${(
			0x1000000 +
			(Math.round((t - R) * p) + R) * 0x10000 +
			(Math.round((t - G) * p) + G) * 0x100 +
			(Math.round((t - B) * p) + B)
		)
			.toString(16)
			.slice(1)}`;
	}

	/**
	 * @param {string} id element id to remove
	 * @returns {boolean} true when there was nothing to remove
	 */
	removeSkin(id) {
		const elem = this.domHelpersSvc.getById(id);
		return elem ? elem.parentNode.removeChild(elem) : true;
	}

	/** @returns {boolean} true when no skin was present */
	resetSkin() {
		if (!this.existSkin()) return true;
		return this.removeSkin('providerTheme') && this.removeSkin('providerVars');
	}

	/**
	 * @param {Types.GenerateSkinParams} opts brand colours
	 * @returns {boolean} true when a skin already existed
	 */
	generateSkin(opts) {
		if (this.existSkin()) return true;

		const rgb = this.hexToRgb(opts.brandPrimary);
		this.colorService.set(rgb[0], rgb[1], rgb[2]);
		this.colorSolverService.setTarget(this.colorService.getAsRGB());
		this.colorSolverService.setTargetHSL(this.colorService.hsl());
		const result = this.colorSolverService.solve();

		// Add Bootstrap 5 CSS variables
		this.addStylesheetVars(opts);

		// Add additional CSS rules
		this.addStylesheetRules([
			// Bootstrap 5 specific overrides
			['.icon.text-primary', ['filter', result.filter]],
		]);

		return true;
	}

	/** @param {Types.StylesheetRule[]} rules */
	addStylesheetRules(rules) {
		const styleEl = this.domHelpersSvc.createElement('style');
		styleEl.setAttribute('media', 'screen');
		styleEl.setAttribute('type', 'text/css');
		styleEl.setAttribute('id', 'providerTheme');

		// Append style element to head
		this.domHelpersSvc.appendChild('head', styleEl);

		// Grab style sheet
		var styleSheet = styleEl.sheet;

		for (let i = 0, rl = rules.length; i < rl; i++) {
			var j = 1,
				rule = rules[i],
				selector = rules[i][0],
				propStr = '';

			// If the second argument of a rule is an array of arrays, correct our variables.
			if (Object.prototype.toString.call(rule[1][0]) === '[object Array]') {
				rule = rule[1];
				j = 0;
			}

			for (let pl = rule.length; j < pl; j++) {
				var prop = rule[j];
				propStr += prop[0] + ':' + prop[1] + (prop[2] ? ' !important' : '') + ';\n';
			}

			// Insert CSS Rule
			styleSheet.insertRule(selector + '{' + propStr + '}', styleSheet.cssRules.length);
		}
	}

	/** @param {Types.GenerateSkinParams} brandColors */
	addStylesheetVars(brandColors) {
		const styleEl = this.domHelpersSvc.createElement('style');
		styleEl.setAttribute('media', 'screen');
		styleEl.setAttribute('type', 'text/css');
		styleEl.setAttribute('id', 'providerVars');

		// Append style element to head
		this.domHelpersSvc.appendChild('head', styleEl);

		// Grab style sheet
		const styleSheet = styleEl.sheet;

		// Convert hex to RGB for variables that need RGB format
		// primary
		const primaryRgb = this.hexToRgb(brandColors.brandPrimary);
		const primaryRgbStr = primaryRgb ? primaryRgb.join(', ') : '33, 37, 41';
		// secondary
		const secondaryRgb = brandColors.brandSecondary ? this.hexToRgb(brandColors.brandSecondary) : null;
		const secondaryRgbStr = secondaryRgb ? secondaryRgb.join(', ') : '108, 117, 125';

		// Create lighter and darker variants for hover states
		// const primaryLighter = this.shadeColor(brandColors.brandPrimary, 0.2);
		const primaryDarker = this.shadeColor(brandColors.brandPrimary, -0.2);

		// Bootstrap 5 CSS variables root
		const root = `:root {
			/* Primary color variables */
			--bs-primary: ${brandColors.brandPrimary};
			--bs-primary-rgb: ${primaryRgbStr};
			--bs-primary-text-emphasis: ${primaryDarker};
			--bs-primary-bg-subtle: ${this.shadeColor(brandColors.brandPrimary, 0.8)};
			--bs-primary-border-subtle: ${this.shadeColor(brandColors.brandPrimary, 0.5)};

			/* Link color */
			--bs-link-color: ${brandColors.brandPrimary};
			--bs-link-color-rgb: ${primaryRgbStr};
			--bs-link-hover-color: ${primaryDarker};

			/* Component colors */
			// --bs-heading-color: ${brandColors.brandPrimary};
			--bs-focus-ring-color: rgba(${primaryRgbStr}, 0.25);

			--bs-secondary: ${brandColors.brandSecondary};
			--bs-secondary-rgb: ${secondaryRgbStr};
			--bs-secondary-text-emphasis: ${secondaryRgb ? this.shadeColor(brandColors.brandSecondary, -0.2) : ''};
			--bs-secondary-bg-subtle: ${secondaryRgb ? this.shadeColor(brandColors.brandSecondary, 0.8) : ''};
			--bs-secondary-border-subtle: ${secondaryRgb ? this.shadeColor(brandColors.brandSecondary, 0.5) : ''};
		}`;

		// Button specific overrides
		const btnPrimary = `.btn-primary {
			--bs-btn-color: #fff;
			--bs-btn-bg: ${brandColors.brandPrimary};
			--bs-btn-border-color: ${brandColors.brandPrimary};
			--bs-btn-hover-color: #fff;
			--bs-btn-hover-bg: ${primaryDarker};
			--bs-btn-hover-border-color: ${this.shadeColor(brandColors.brandPrimary, -0.25)};
			--bs-btn-focus-shadow-rgb: ${primaryRgbStr};
			--bs-btn-active-color: #fff;
			--bs-btn-active-bg: ${this.shadeColor(brandColors.brandPrimary, -0.3)};
			--bs-btn-active-border-color: ${this.shadeColor(brandColors.brandPrimary, -0.35)};
			--bs-btn-active-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
			--bs-btn-disabled-color: #fff;
			--bs-btn-disabled-bg: ${brandColors.brandPrimary};
			--bs-btn-disabled-border-color: ${brandColors.brandPrimary};
		}`;

		const btnOutlinePrimary = `.btn-outline-primary {
			--bs-btn-color: ${brandColors.brandPrimary};
			--bs-btn-border-color: ${brandColors.brandPrimary};
			--bs-btn-hover-color: #fff;
			--bs-btn-hover-bg: ${brandColors.brandPrimary};
			--bs-btn-hover-border-color: ${brandColors.brandPrimary};
			--bs-btn-focus-shadow-rgb: ${primaryRgbStr};
			--bs-btn-active-color: #fff;
			--bs-btn-active-bg: ${brandColors.brandPrimary};
			--bs-btn-active-border-color: ${brandColors.brandPrimary};
			--bs-btn-active-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
			--bs-btn-disabled-color: ${brandColors.brandPrimary};
			--bs-btn-disabled-bg: transparent;
			--bs-btn-disabled-border-color: ${brandColors.brandPrimary};
		}`;

		// Pagination
		const pagination = `.pagination {
			--bs-pagination-active-bg: ${brandColors.brandPrimary};
			--bs-pagination-active-border-color: ${brandColors.brandPrimary};
			--bs-pagination-active-color: #fff;
			--bs-pagination-color: ${brandColors.brandPrimary};
			--bs-pagination-hover-color: ${primaryDarker};
			--bs-pagination-focus-color: ${primaryDarker};
			--bs-pagination-focus-box-shadow: 0 0 0 0.25rem rgba(${primaryRgbStr}, 0.25);
		}`;

		// Nav pills
		const navPills = `.nav-pills {
			--bs-nav-pills-link-active-color: ${brandColors.brandPrimary};
			--bs-nav-pills-link-active-bg: ${this.shadeColor(brandColors.brandPrimary, 0.92)};
		}`;

		// List group active state
		const listGroup = `.list-group {
			--bs-list-group-active-bg: ${brandColors.brandPrimary};
			--bs-list-group-active-border-color: ${brandColors.brandPrimary};
		}`;

		// Dropdown active state
		const dropdown = `.dropdown-menu {
			--bs-dropdown-link-active-bg: ${brandColors.brandPrimary};
		}`;

		// Progress bar
		const progressBar = `.progress {
			--bs-progress-bar-bg: ${brandColors.brandPrimary};
		}`;

		// Accent color for native form elements
		const accentColor = `input[type="range"], input[type="checkbox"], input[type="radio"] {
			accent-color: ${brandColors.brandPrimary};
		}`;

		// Insert CSS Rules
		styleSheet.insertRule(root, styleSheet.cssRules.length);
		styleSheet.insertRule(btnPrimary, styleSheet.cssRules.length);
		styleSheet.insertRule(btnOutlinePrimary, styleSheet.cssRules.length);
		styleSheet.insertRule(pagination, styleSheet.cssRules.length);
		styleSheet.insertRule(navPills, styleSheet.cssRules.length);
		styleSheet.insertRule(listGroup, styleSheet.cssRules.length);
		styleSheet.insertRule(dropdown, styleSheet.cssRules.length);
		styleSheet.insertRule(progressBar, styleSheet.cssRules.length);
		styleSheet.insertRule(accentColor, styleSheet.cssRules.length);

		// Add secondary button styles if secondary color is provided
		if (brandColors.brandSecondary) {
			// const secondaryLighter = this.shadeColor(brandColors.brandSecondary, 0.2);
			const secondaryDarker = this.shadeColor(brandColors.brandSecondary, -0.2);

			const btnSecondary = `.btn-secondary {
				--bs-btn-color: #fff;
				--bs-btn-bg: ${brandColors.brandSecondary};
				--bs-btn-border-color: ${brandColors.brandSecondary};
				--bs-btn-hover-color: #fff;
				--bs-btn-hover-bg: ${secondaryDarker};
				--bs-btn-hover-border-color: ${this.shadeColor(brandColors.brandSecondary, -0.25)};
				--bs-btn-focus-shadow-rgb: ${secondaryRgbStr};
				--bs-btn-active-color: #fff;
				--bs-btn-active-bg: ${this.shadeColor(brandColors.brandSecondary, -0.3)};
				--bs-btn-active-border-color: ${this.shadeColor(brandColors.brandSecondary, -0.35)};
				--bs-btn-active-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
				--bs-btn-disabled-color: #fff;
				--bs-btn-disabled-bg: ${brandColors.brandSecondary};
				--bs-btn-disabled-border-color: ${brandColors.brandSecondary};
			}`;

			styleSheet.insertRule(btnSecondary, styleSheet.cssRules.length);
		}

		// Dark mode overrides — adjust brand colors for contrast on dark backgrounds
		const primaryLighter = this.shadeColor(brandColors.brandPrimary, 0.3);
		const darkRoot = `[data-bs-theme="dark"] {
			--bs-primary: ${primaryLighter};
			--bs-primary-rgb: ${this.hexToRgb(primaryLighter)?.join(', ') || primaryRgbStr};
			--bs-primary-text-emphasis: ${this.shadeColor(brandColors.brandPrimary, 0.4)};
			--bs-primary-bg-subtle: ${this.shadeColor(brandColors.brandPrimary, -0.6)};
			--bs-primary-border-subtle: ${this.shadeColor(brandColors.brandPrimary, -0.3)};
			--bs-link-color: ${primaryLighter};
			--bs-link-color-rgb: ${this.hexToRgb(primaryLighter)?.join(', ') || primaryRgbStr};
			--bs-link-hover-color: ${this.shadeColor(brandColors.brandPrimary, 0.5)};
			--bs-heading-color: ${primaryLighter};
			--bs-focus-ring-color: rgba(${this.hexToRgb(primaryLighter)?.join(', ') || primaryRgbStr}, 0.25);
		}`;
		styleSheet.insertRule(darkRoot, styleSheet.cssRules.length);

		const darkBtnPrimary = `[data-bs-theme="dark"] .btn-primary {
			--bs-btn-bg: ${brandColors.brandPrimary};
			--bs-btn-border-color: ${brandColors.brandPrimary};
			--bs-btn-hover-bg: ${primaryLighter};
			--bs-btn-hover-border-color: ${primaryLighter};
			--bs-btn-active-bg: ${this.shadeColor(brandColors.brandPrimary, 0.4)};
			--bs-btn-active-border-color: ${this.shadeColor(brandColors.brandPrimary, 0.4)};
		}`;
		styleSheet.insertRule(darkBtnPrimary, styleSheet.cssRules.length);

		const darkBtnOutline = `[data-bs-theme="dark"] .btn-outline-primary {
			--bs-btn-color: ${primaryLighter};
			--bs-btn-border-color: ${primaryLighter};
			--bs-btn-hover-bg: ${primaryLighter};
			--bs-btn-hover-border-color: ${primaryLighter};
			--bs-btn-active-bg: ${primaryLighter};
			--bs-btn-active-border-color: ${primaryLighter};
		}`;
		styleSheet.insertRule(darkBtnOutline, styleSheet.cssRules.length);
	}

	/** @returns {boolean} true when either provider style element is present */
	existSkin() {
		return (
			this.domHelpersSvc.getById('providerTheme') !== null ||
			this.domHelpersSvc.getById('providerVars') !== null
		);
	}

	/**
	 * Initialize theme on app bootstrap.
	 * Priority: sessionStorage override > OS prefers-color-scheme > light (default).
	 * sessionStorage is shared across tabs in the same session.
	 * Also registers a listener for live OS changes.
	 */
	initTheme() {
		// Prevent transition flash on initial load
		document.documentElement.classList.add('no-transition');
		const saved = sessionStorage.getItem('oca-theme');
		const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false;
		// sessionStorage wins over OS preference; if neither exists, default to light
		const theme = saved ?? (prefersDark ? 'dark' : 'light');
		this._applyTheme(theme);
		/* Re-enable transitions after paint
		 How It Works: first rAF queues a callback for the frame right before the browser performs its next paint. At this point, the DOM changes (adding the theme class and removing .no-transition) are staged together, so the browser would still try to transition the styles in that single frame.
		 second (Nested) rAF: forces the execution into the frame after the initial painted state. This guarantees the theme styles render without transitions applied before .no-transition is stripped away. */
		window.requestAnimationFrame(() => {
			window.requestAnimationFrame(() => {
				document.documentElement.classList.remove('no-transition');
			});
		});
		this._listenToSystemChanges();
	}

	/** @returns {'light'|'dark'} */
	getTheme() {
		return document.documentElement.getAttribute('data-bs-theme') || 'light';
	}

	/**
	 * Apply theme visually without persisting.
	 * Used by initTheme, OS listener, and resetToSystem.
	 * @param {'light'|'dark'} theme
	 */
	_applyTheme(theme) {
		document.documentElement.setAttribute('data-bs-theme', theme);
		document.documentElement.style.colorScheme = theme;
		this._updateMetaThemeColor(theme);
	}

	/**
	 * Set theme with persistence — only called on explicit user action.
	 * @param {'light'|'dark'} theme
	 */
	setTheme(theme) {
		this._applyTheme(theme);
		sessionStorage.setItem('oca-theme', theme);
	}

	toggleTheme(value) {
		const current = this.getTheme();
		const next = value || (current === 'dark' ? 'light' : 'dark');
		if (next === current) return current;

		this.setTheme(next);
		return next;
	}

	/**
	 * Clear user override and revert to OS preference.
	 */
	resetToSystem() {
		sessionStorage.removeItem('oca-theme');
		const os = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
		this._applyTheme(os);
	}

	/** @returns {boolean} */
	isDark() {
		return this.getTheme() === 'dark';
	}

	/**
	 * Update <meta name="theme-color"> to match the current theme.
	 * Light mode uses the provider's brand primary color from the authenticated user,
	 * falling back to colorVars.brandPrimary.
	 * @param {'light'|'dark'} theme
	 */
	_updateMetaThemeColor(theme) {
		const meta = document.querySelector('meta[name="theme-color"]');
		if (!meta) return;
		if (theme === 'dark') {
			meta.setAttribute('content', this.META_THEME_DARK);
		} else {
			const user = this.AuthSvc.currentUser();
			const lightColor = user?.theme?.primary_color || this.colorVars.brandPrimary;
			meta.setAttribute('content', lightColor);
		}
	}

	/**
	 * Listen for OS prefers-color-scheme changes.
	 * Only applies when user has no explicit preference saved in sessionStorage.
	 */
	_listenToSystemChanges() {
		if (this._mediaListener) return; // already registered
		const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
		if (!mq) return; // matchMedia unavailable — nothing to listen to
		this._mediaListener = e => {
			const saved = sessionStorage.getItem('oca-theme');
			if (!saved) {
				this._applyTheme(e.matches ? 'dark' : 'light');
			}
		};
		mq.addEventListener('change', this._mediaListener);
	}

	/** Remove the OS listener (useful for testing/cleanup) */
	destroyListeners() {
		if (this._mediaListener) {
			const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
			mq?.removeEventListener('change', this._mediaListener);
			this._mediaListener = null;
		}
	}

	/**
	 * @param {string} hex hex colour, shorthand or full
	 * @returns {[number, number, number] | null} rgb triple, or null when invalid
	 */
	hexToRgb(hex) {
		if (!hex) return null;

		// Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
		const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
		const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);

		return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
	}
}
//
let compMod = angular
	.module('mod.svc.ThemingService', [AuthSvcMod, DOMHelpersSvcMod])
	.service('ColorSvc', Color)
	.service('ColorSolverSvc', ColorSolver)
	.service('ThemingService', Theming);
export default (compMod = compMod.name);
